import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import MomentUtils from "@date-io/moment";
import { createTheme, ThemeProvider } from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { authClient, AuthProvider, getAccessToken } from "auth";
import React, { FC } from "react";
import { Authentication } from "Views/Components/Authentication/Authentication";
import { MainPage } from "Views/MainPage/MainPage";
import "./App.scss";

const httpLink = new HttpLink({ uri: process.env.REACT_APP_HASURA_URL });
const authLink = setContext(async (_, { headers }) => {
  const token = await getAccessToken();
  const tokenParsed = authClient.tokenParsed;
  const hasuraRolesClaim = "https://hasura.io/jwt/claims";
  const hasuraDefaultRolesClaim = "x-hasura-default-role";
  const activeRole = tokenParsed?.[hasuraRolesClaim]?.[hasuraDefaultRolesClaim] || "";

  return {
    headers: {
      ...headers,
      ...(activeRole ? { "X-Hasura-Role": activeRole } : {}),
      ...(tokenParsed?.oid ? { "X-Hasura-User-Id": tokenParsed?.oid } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
  };
});

const apolloClient = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
});

const customTheme = createTheme({
  palette: {
    primary: {
      main: "#2C58A4",
    },
  },
  typography: {
    fontFamily: '"Red Hat Display", sans-serif',
    h4: {
      fontSize: 24,
      fontStyle: "italic",
    },
    h5: {
      fontSize: 20,
    },
    h6: {
      fontSize: 24,
    },
    subtitle1: {
      fontSize: 19,
      fontWeight: "bold",
    },
    body1: {
      fontSize: 16,
    },
    body2: {
      fontSize: 16,
    },
    subtitle2: {
      fontSize: 14,
      fontStyle: "italic",
    },
  },
  overrides: {
    MuiTooltip: {
      tooltip: {
        fontFamily: '"Red Hat Display", sans-serif',
        fontSize: 14,
        color: "black",
        backgroundColor: "white",
        boxShadow: "0 20px 80px 0",
      },
      arrow: {
        color: "white",
      },
    },
  },
});

export const App: FC = () => {
  return (
    <AuthProvider>
      <ApolloProvider client={apolloClient}>
        <ThemeProvider theme={customTheme}>
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <Authentication>
              <CssBaseline />
              <MainPage />
            </Authentication>
          </MuiPickersUtilsProvider>
        </ThemeProvider>
      </ApolloProvider>
    </AuthProvider>
  );
};
